/* eslint-disable id-length */
import {
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewContainerRef,
} from "@angular/core";
import { theme } from "@vattenfall/design-system";
import { isEmpty, isNullable } from "@vattenfall/util";
import {
  Axis,
  Chart,
  DashStyleValue,
  GradientColorStopObject,
  Options,
  Series,
  SeriesOptionsType,
  SeriesZonesOptionsObject,
  TooltipFormatterContextObject,
  TooltipOptions,
  XAxisOptions,
  XAxisPlotBandsOptions,
  XAxisPlotLinesOptions,
  XrangePointOptionsObject,
  YAxisOptions,
} from "highcharts";
import { Subscription } from "rxjs";

/**
 * Key-value hash holding gradient coloring information in a format that can be consumed by Highchart
 */
const GRADIENTS = <const>{
  "blue-red": [
    [0, theme.colors.energysource.gas.DEFAULT],
    [1, theme.colors.primary.blue.DEFAULT],
  ],
  "blue-yellow": [
    [0, theme.colors.primary.yellow.DEFAULT],
    [1, theme.colors.primary.blue.DEFAULT],
  ],
  "black-gray": [
    [0, theme.colors.primary.gray["70"]],
    [1, theme.colors.primary.black.DEFAULT],
  ],
};

/**
 * Type guard that checks whether the provided value is a valid gradient color code
 */
const isGradient = (value: string): value is Gradient => value in GRADIENTS;

export type Gradient = keyof typeof GRADIENTS;

export type Color =
  | "green"
  | "darkgreen"
  | "blue"
  | "darkblue"
  | "pink"
  | "darkpurple"
  | "red"
  | "purple"
  | "yellow"
  | "white"
  | "black"
  | "gray"
  | "hydro"
  | "wind"
  | "solar"
  | "biomass"
  | "coal"
  | "gas"
  | "nuclear"
  | "district";
export type Dash = "solid" | "dot" | "dash" | "shortdash";
export type Type = "spline" | "column" | "pie" | "bar" | "line";

const colors = { ...theme.colors.primary, ...theme.colors.secondary, ...theme.colors.energysource };

type Highcharts = { chart: (element: Element, options: Options) => Chart };

/** Check if values of simple changes has changed. */
function hasChanged(simpleChanges: SimpleChanges) {
  return Object.values(simpleChanges).some(({ previousValue, currentValue }) => {
    if (Array.isArray(previousValue) && Array.isArray(currentValue))
      return previousValue.length !== currentValue.length || JSON.stringify(previousValue) !== JSON.stringify(currentValue);
    return previousValue !== currentValue;
  });
}

@Directive({
  selector: "ui-chart-series",
})
export class SeriesDirective implements OnInit, OnChanges {
  /**
   * Series chart data represented as a range of numbers
   */
  @Input() data: Array<[string, number]> = [];
  /**
   * Series line color. Default is `darkgreen`.
   */
  @Input() color: Color | Gradient = "darkgreen";
  /**
   * Series line opacity. Default is "DEFAULT", which means no opacity
   */
  @Input() opacity: "DEFAULT" | "70" | "50" | "30" | "10" = "DEFAULT";
  /**
   * Series line dash style. Default is `solid`;
   */
  @Input() dash: Dash = "solid";
  /**
   * Series chart type. Default is `spline`;
   */
  @Input() type: Type = "spline";
  /**
   * If series should animate when added on the chart.
   */
  @Input() animation = true;
  /**
   * Slice chart data into a projected section represented by a dash line. Applicable for line charts.
   */
  @Input() projection?: number;
  /**
   * Whether the series should be visible in view. Default is `true`.
   */
  @Input() visible = true;

  id = Math.random().toString();

  private _axisDirective?: AxisDirective;
  get axisDirective() {
    return this._axisDirective;
  }

  constructor(private viewContainerRef: ViewContainerRef, public elementRef: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    if (!isNullable(this.projection) && isGradient(this.color)) {
      console.warn("Gradient is not supported for projection series. Please use a solid color instead.");
    }
    this._axisDirective = this.viewContainerRef.injector.get(AxisDirective);
  }
  ngOnChanges(simpleChanges: SimpleChanges) {
    const chartComponent = this.viewContainerRef.injector.get(ChartComponent);
    // Wait until chart has been instantiated
    if (chartComponent?.chart && hasChanged(simpleChanges)) {
      const series = chartComponent.chart.series.find((series) => series.userOptions.id === this.id) as Series;

      // Repaint the graph
      if (series) {
        // Reconstruct options based on new input
        const seriesOptions = chartComponent.getOptions().series as Array<SeriesOptionsType>;
        const options = seriesOptions.find((seriesOptions) => seriesOptions.id === this.id) as SeriesOptionsType;
        series.update(options, true);
        // Repaint the categories, as update doesn't repaint categories with new data set.
        const xAxisOptions = chartComponent.getOptions().xAxis as Array<XAxisOptions>;
        series.xAxis.setCategories([...xAxisOptions].shift()?.categories as Array<string>);
      }
    }
  }
}

@Directive({
  selector: "ui-chart-axis",
})
export class AxisDirective implements OnChanges {
  /**
   * Unit suffix that will be added after axis value
   */
  @Input() unit = "";
  /**
   * Number of decimals shown in tooltip after axis value
   */
  @Input() tooltipDecimals = 2;
  /**
   * Whether the axis should be visible in view. Default is `true`.
   */
  @Input() visible = true;
  /**
   * Indicates on which side the axis should be placed. Default is `left`.
   */
  @Input() align: "left" | "right" = "left";

  id = Math.random().toString();

  constructor(private viewContainerRef: ViewContainerRef, public elementRef: ElementRef<HTMLElement>) {}
  ngOnChanges(simpleChanges: SimpleChanges) {
    const chartComponent = this.viewContainerRef.injector.get(ChartComponent);
    // Wait until chart has been instantiated
    if (chartComponent?.chart && hasChanged(simpleChanges)) {
      const xAxis = chartComponent.chart.xAxis.find((xAxis) => xAxis.userOptions.id === this.id) as Axis;
      // Reconstruct options based on new input
      const xAxisOptions = chartComponent.getOptions().xAxis as Array<XAxisOptions>;
      const options = xAxisOptions.find((xAxisOptions) => xAxisOptions.id === this.id) as XAxisOptions;
      // Repaint the graph
      xAxis?.update(options, true);
    }
  }
}
/**
 * Vattenfall UI `ui-chart-plotline` is a container for rendering plotlines in chart components.
 *
 * TODO (stefan): This example seems to be incorrect. From and to should be the index positions and not the values it seems?
 *
 * @example
 *  <ui-chart-plotline [value]="2014" [color]="'darkgreen'" [dash]="'solid'" [width]="1"></ui-chart-plotline>
 *  Will render a plotline at 2014 with a darkgreen color, solid dash and width 1.
 */
@Directive({
  selector: "ui-chart-plotline",
})
export class PlotlineDirective {
  /**
   * Plotline background color. Default is `darkgreen`.
   */
  @Input() color: Color = "darkgreen";
  /**
   * Plotline line dash style. Default is `solid`;
   */
  @Input() dash: Dash = "solid";
  /**
   * Plotline line dash width. Default is `1`;
   */
  @Input() width = 1;
  /**
   * Plotline value;
   */
  @Input() value?: number;
}

/**
 * Vattenfall UI `ui-chart-plotband` is a container for rendering plotbands in chart components.
 * Plotband is a vertical band that marks an area in the chart. It can be used to highlight a range of values, for example high and low values
 *
 * TODO (stefan): This example seems to be incorrect. From and to should be the index positions and not the values it seems?
 *
 * @example
 * <ui-chart-plotband [from]="2014" [to]="2015" [color]="'red'"></ui-chart-plotband>
 * This will render a plotband from 2014 to 2015 with a red color.
 */
@Directive({
  selector: "ui-chart-plotband",
})
export class PlotbandDirective {
  /**
   * Plotband background color. Default is `darkgreen`.
   */
  @Input() color: Color = "darkgreen";
  /**
   * The start position of the plot band in axis units.
   */
  @Input() from?: number;
  /**
   * The end position of the plot band in axis units.
   */
  @Input() to?: number;
}

/**
 * Vattenfall UI `ui-chart` is a container for rendering chart components.
 *
 * This component is a wrapper used for rendering [Highcharts](https://www.highcharts.com) charts that follows Vattenfall brand guidlines. It takes takes one or two `<ui-chart-axis>` components where each accepts one or several `<ui-chart-series>` components.
 *
 * Use `pointStart` to mark the start of a range of values (typically the chart x axis), for example if a series contains one yearly value starting from 1945, set `pointStart` to 1945.
 *
 * Optionally, an [Highchart configuration object](https://api.highcharts.com/highcharts) can be provided. This allows for full flexibility to style and configure the behavior of the chart component.
 */
@Component({
  selector: "ui-chart",
  template: "",
})
export class ChartComponent implements OnInit, OnDestroy {
  /**
   * Defines on what value to start. For example, if a series contains one yearly value starting from 1945, set `pointStart` to 1945. Default is `0`.
   */
  @Input() pointStart: number | string | Date = 0;
  /**
   * The categories represent labels for x-axis, If categories are present for the xAxis, names are used instead of numbers for that axis. `categories` have higher Precedence than `pointStart` if it has values. Defaults to undefined.
   */
  @Input() categories?: Array<string>;
  /**
   * Highcharts configuration options object. Allows you to override the default values by passing chart configuration as a data object. Read the [documentation](https://api.highcharts.com/highcharts) for more details.
   */
  @Input() options: Options = {};
  /**
   * Whether the chart should be rendered with markers. Default is `true`.
   */
  @Input() showMarkers = true;

  @ContentChildren(AxisDirective, { descendants: true }) private readonly axisDirectives!: QueryList<AxisDirective>;
  @ContentChildren(SeriesDirective, { descendants: true }) private readonly seriesDirectives!: QueryList<SeriesDirective>;
  @ContentChildren(PlotlineDirective, { descendants: true })
  private readonly plotlineDirectives!: QueryList<PlotlineDirective>;
  @ContentChildren(PlotbandDirective, { descendants: true }) private readonly plotbandDirectives!: QueryList<PlotbandDirective>;

  chart?: Chart;

  private isOptionsProvided = false;

  highcharts!: Highcharts;

  private seriesDirectivesSubscription = Subscription.EMPTY;
  private axisDirectivesSubscription = Subscription.EMPTY;
  private plotbandDirectivesSubscription = Subscription.EMPTY;
  private plotlineDirectivesSubscription = Subscription.EMPTY;

  constructor(private elementRef: ElementRef) {}

  async ngOnInit() {
    if (!isEmpty(this.options)) this.isOptionsProvided = true;
    // Make sure that the host element spans the width of its parent
    this.elementRef.nativeElement.style.display = "block";
    // this.elementRef.nativeElement.style.width = "100%";
    this.highcharts = (await import("highcharts").then(
      (commonJsModule) => commonJsModule.default ?? commonJsModule
    )) as Highcharts;
    this.options = this.getOptions();
    // Instantiate the graph with incomplete options
    this.chart = this.highcharts.chart(this.elementRef.nativeElement, this.options as Options);
    // Redraws chart when <ui-chart-series> or <ui-chart-axis> elements are added or removed from DOM
    this.seriesDirectivesSubscription = this.seriesDirectives.changes.subscribe(this.updateChart);
    this.axisDirectivesSubscription = this.axisDirectives.changes.subscribe(this.updateChart);
    this.plotbandDirectivesSubscription = this.plotbandDirectives.changes.subscribe(this.updateChart);
    this.plotlineDirectivesSubscription = this.plotlineDirectives.changes.subscribe(this.updateChart);
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.seriesDirectivesSubscription.unsubscribe();
    this.axisDirectivesSubscription.unsubscribe();
    this.plotbandDirectivesSubscription.unsubscribe();
    this.plotlineDirectivesSubscription.unsubscribe();
  }
  /**
   * Reconstruct options and and repaint graph.
   *
   * Called whenever <ui-chart-series> or <ui-chart-axis> updates its properties or are added or removed from DOM.
   */
  updateChart = () => {
    this.options = this.getOptions();
    this.chart?.update(this.options, true, true);
  };

  getOptions = (): Options => {
    // Override default configurations if an options object has been passed
    if (this.isOptionsProvided) return this.options;
    else
      return {
        title: undefined,
        series: this.getSeries(this.seriesDirectives),
        yAxis: this.getYAxis(this.axisDirectives),
        xAxis: this.getXAxis(this.categories),
        tooltip: this.getTooltip(),
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 600,
              },
              chartOptions: {
                chart: {
                  height: 550,
                  marginLeft: 1,
                  marginRight: 1,
                },
                yAxis: this.getYAxisMobile(this.axisDirectives),
                xAxis: {
                  startOnTick: false,
                  endOnTick: false,
                },
              },
            },
          ],
        },
        plotOptions: {
          series: {
            pointStart: this.pointStart,
            marker: {
              enabled: this.showMarkers,
              symbol: "circle",
            },
            dataLabels: {
              // Disable to show value on each point in series
              enabled: false,
            },
            align: "left",
            events: {
              // Disables the ability to add/remove series from chart
              // by clicking on the serie's legend, the default behavior
              // is that series are added or removed cy the inclusion or
              // removal of <ui-chart-series> elements
              // eslint-disable-next-line ramda/prefer-ramda-boolean
              legendItemClick: function () {
                return false;
              },
              /* Add attribute `data-e2e-animation-completed` once animation
              on the graph is completed. This behaviour help us to capture
              imagesnapshot untill the series in graph is update with animation.*/
              afterAnimate: () => {
                this.elementRef.nativeElement.setAttribute("data-e2e-animation-completed", "");
              },
            },
          },
        },
        legend: {
          itemStyle: {
            fontWeight: theme.fontWeight.regular,
            color: theme.colors.primary.black.DEFAULT,
          },
        },
        chart: {
          style: {
            fontFamily: theme.fontFamily.regular.join(", "),
          },
          events: {
            // Remove attribute `data-e2e-animation-completed` if already present,
            // and add it again after animation is completed on graph
            load: () => {
              if (this.elementRef.nativeElement.hasAttribute("data-e2e-animation-completed"))
                this.elementRef.nativeElement.removeAttribute("data-e2e-animation-completed");
            },
          },
        },
        credits: {
          enabled: false,
        },
      } as Options;
  };

  getSeries(seriesDirectives: QueryList<SeriesDirective>): Array<SeriesOptionsType> {
    const getZones = (seriesDirective: SeriesDirective): Array<SeriesZonesOptionsObject> => {
      const zones: SeriesZonesOptionsObject[] = [];
      if (!isNullable(seriesDirective.projection)) zones.push({ value: seriesDirective.projection }, { dashStyle: "ShortDash" });
      if (isGradient(seriesDirective.color))
        zones.push({
          color: {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: GRADIENTS[seriesDirective.color] as unknown as GradientColorStopObject[],
          },
        });
      return zones;
    };

    const getYAxis = (seriesDirective: SeriesDirective) => {
      return this.axisDirectives.first === seriesDirective.axisDirective ? 0 : 1;
    };
    const getTooltip = (seriesDirective: SeriesDirective) => {
      return { valueSuffix: seriesDirective.axisDirective?.unit, valueDecimals: seriesDirective.axisDirective?.tooltipDecimals };
    };
    const getDataLabels = (seriesDirective: SeriesDirective) => {
      return { format: "{y} " + seriesDirective.axisDirective?.unit };
    };
    const getInactiveState = (seriesDirective: SeriesDirective) => {
      // If at least one serie in the graph has opacity set, decrease the opacity for inactive state (whenhover on serie in legend).
      // This is done in order not to get similar colors for the active/inactive columns.
      // Set this serie's inactive opacity higher if it already has opacity or it will be close to invisible.
      const graphHasAtLeastOneSerieWithOpacity = seriesDirectives.some((serie) => serie.opacity !== "DEFAULT");
      const thisSerieHasOpacity = seriesDirective.opacity === "DEFAULT";
      if (graphHasAtLeastOneSerieWithOpacity) {
        return { inactive: { opacity: thisSerieHasOpacity ? 0.1 : 0.05 } };
      }
      return { inactive: { opacity: 0.2 } }; // default value in highchart
    };
    const getDynamicProperties = (seriesDirective: SeriesDirective) => {
      const yAxis = getYAxis(seriesDirective);
      const tooltip = getTooltip(seriesDirective);
      const zones = getZones(seriesDirective);
      const dataLabels = getDataLabels(seriesDirective);
      const id = seriesDirective.id;
      const name = seriesDirective.elementRef.nativeElement.innerText.trim();
      const data = seriesDirective.data as Array<XrangePointOptionsObject>;
      const color = !isGradient(seriesDirective.color)
        ? colors[seriesDirective.color][seriesDirective.opacity]
        : GRADIENTS[seriesDirective.color];
      const dashStyle = seriesDirective.dash as DashStyleValue;
      const type = seriesDirective.type;
      const visible = seriesDirective.visible;
      const animation = seriesDirective.animation ? { duration: 250 } : false;
      const states = getInactiveState(seriesDirective);
      return { id, visible, yAxis, tooltip, zones, name, data, dataLabels, color, dashStyle, type, animation, states };
    };
    return seriesDirectives.map((seriesDirective) => {
      return {
        ...getDynamicProperties(seriesDirective),
        lineWidth: 3,
        zoneAxis: "x",
        step: "left",
        clip: false,
      };
    }) as SeriesOptionsType[];
  }

  getYAxis(axisDirectives: QueryList<AxisDirective>): Array<YAxisOptions> {
    return axisDirectives.map((axisDirective, index) => {
      return {
        lineWidth: 1,
        title: undefined,
        id: axisDirective.id,
        opposite: index % 2 !== 0,
        lineColor: theme.colors.tertiary.lightgrey,
        gridLineColor: theme.colors.tertiary.lightergrey,
        gridLineWidth: 1,
        gridLineDashStyle: "LongDash",
        visible: axisDirective.visible,
        className: "vattenfall-elements",
        labels: {
          useHTML: true,
          formatter: function () {
            return `<span class="ds-rounded-lg ds-p-[2px] ds-bg-primary-white/75">${this.value} ${axisDirective.unit}</span>`;
          },
          style: {
            color: theme.colors.primary.black.DEFAULT,
          },
        },
      };
    });
  }
  /**
   * This function is introduced to help the graph identify on how to render the graph on mobile
   * @param axisDirectives
   * @returns
   */
  getYAxisMobile(axisDirectives: QueryList<AxisDirective>): Array<YAxisOptions> {
    return axisDirectives.map((axisDirective) => {
      return {
        labels: {
          align: axisDirective.align,
          x: 1,
          y: -1,
          // the width of the label must be specified to prevent line breaks in the mobile labels
          style: {
            width: 100,
          },
        },
      };
    });
  }

  getXAxis(categories?: Array<string>): Array<XAxisOptions> {
    return [
      {
        categories,
        lineColor: theme.colors.tertiary.lightgrey,
        gridLineColor: theme.colors.tertiary.lightergrey,
        // TODO: Should probably be optional?
        gridLineWidth: 1,
        gridLineDashStyle: "LongDash",
        tickInterval: 1,
        // Place vertical grid lines just above category labels
        startOnTick: true,
        endOnTick: true,
        tickmarkPlacement: "on",
        labels: {
          style: {
            color: theme.colors.primary.black.DEFAULT,
          },
        },
        plotLines: this.getPlotLines(),
        plotBands: this.getPlotBands(),
      },
    ];
  }

  getPlotLines(): Array<XAxisPlotLinesOptions> {
    return this.plotlineDirectives.map((plotlineDirective: PlotlineDirective) => {
      return {
        width: 2,
        zIndex: 5, // maybe this should be configurable
        value: plotlineDirective.value,
        color: colors[plotlineDirective.color].DEFAULT,
        dashStyle: plotlineDirective.dash as DashStyleValue,
      };
    });
  }

  getPlotBands(): Array<XAxisPlotBandsOptions> {
    return this.plotbandDirectives.map((plotbandDirective) => {
      return {
        zIndex: 0,
        to: plotbandDirective.to,
        from: plotbandDirective.from,
        color: `${colors[plotbandDirective.color].DEFAULT}10`,
      };
    });
  }

  getTooltip(): TooltipOptions {
    return {
      shadow: true,
      shared: true,
      split: true,
      borderColor: theme.colors.primary.white,
      backgroundColor: theme.colors.primary.white,
      outside: true,
      useHTML: true,
      className: "vattenfall-elements",
      formatter: function () {
        const keys: Array<number> = [];
        return this.points
          ?.sort((currentSeries, nextSeries) => {
            if (new Date(nextSeries.point.name) instanceof Date && new Date(currentSeries.point.name) instanceof Date)
              return new Date(nextSeries.point.name).valueOf() - new Date(currentSeries.point.name).valueOf();
            return 0; // Do not sort array if instance is not date.
          })
          .reduce((tooltipMarkup: string, tooltipFormatterContextObject: TooltipFormatterContextObject) => {
            const getKey = () => {
              if (!keys.includes(tooltipFormatterContextObject.key)) {
                keys.push(tooltipFormatterContextObject.key);
                return `<span class='ds-block ds-font-bold ds-my-2xs'>${tooltipFormatterContextObject.key}</span>`;
              }
              return "";
            };
            const series = tooltipFormatterContextObject.point.series as Series & {
              tooltipOptions: { valueSuffix: string; valueDecimals: number };
            };
            const getPath = () => {
              if (series.type === "spline")
                return `<path fill='none' d='M 0 11 L 16 11' stroke='${tooltipFormatterContextObject.point.color}' stroke-width='3'></path>
	                    <path fill='${tooltipFormatterContextObject.point.color}' d="M 8 15 A 4 4 0 1 1 8.003999999333336 14.999998000000167 Z" opacity="1"></path>`;
              return `<circle cx='8' cy='10' r='5' fill='${tooltipFormatterContextObject.point.color}' />`;
            };
            return `
              ${tooltipMarkup}
              ${getKey()}
              <span class='ds-align-middle'>
              <svg width='20' height='20' class='ds-inline-block'>
                  ${getPath()}
                </svg>
              </span>
              <span class='ds-align-middle'>
                ${tooltipFormatterContextObject.y.toFixed(series.tooltipOptions.valueDecimals)} ${
              series.tooltipOptions.valueSuffix
            }
              </span>
              <br />
          `;
          }, "");
      },
      style: {
        // default font size similar to y-axis units
        fontSize: "11px",
      },
    };
  }
}
